import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import SEOHead from "../components/SEOHead";
import { useLanguage } from "../context/LanguageContext";
import { SITE } from "../lib/constants";

const ShoulderEmergencyPage = () => {
  const { lang } = useLanguage();
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const isFr = lang === "fr";

  const title = isFr
    ? "Urgences de l'épaule à Marseille — Dr Richard Aswad | IMOTEpS"
    : "Shoulder emergencies in Marseille — Dr Richard Aswad | IMOTEpS";
  const description = isFr
    ? "Luxation, fracture, traumatisme aigu de l'épaule à Marseille (13008) : conduite à tenir, examens à réaliser, prise en charge par le Dr Richard Aswad — chirurgien spécialiste."
    : "Dislocation, fracture, acute shoulder trauma in Marseille (13008): what to do, exams to perform, care by Dr Richard Aswad — specialist surgeon.";

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "MedicalWebPage",
    name: title,
    description,
    inLanguage: isFr ? "fr-FR" : "en-US",
    about: {
      "@type": "MedicalCondition",
      name: isFr ? "Traumatismes aigus de l'épaule" : "Acute shoulder trauma",
    },
    mainEntity: {
      "@type": "Physician",
      name: "Dr Richard Aswad",
      medicalSpecialty: ["Orthopedic", "Emergency Medicine"],
      url: "https://imoteps.doctor/",
      telephone: SITE.phone,
    },
  };

  return (
    <>
      <SEOHead
        title={title}
        description={description}
        canonical="/urgences-epaule"
        keywords="urgences épaule Marseille, luxation épaule que faire, fracture clavicule Marseille, traumatisme épaule chute, chirurgien épaule urgent Marseille, Dr Aswad urgences"
        lang={lang}
        jsonLd={jsonLd}
      />

      <main data-testid="shoulder-emergency-page" className="bg-white pt-28 md:pt-36 pb-20">
        <article className="container-page max-w-4xl">
          <nav aria-label="Breadcrumb" className="text-xs text-muted-foreground">
            <Link to="/" className="hover:text-[#FF5A1F]">{isFr ? "Accueil" : "Home"}</Link>
            <span className="mx-2">/</span>
            <span className="text-[#FF5A1F] font-semibold">{isFr ? "Urgences de l'épaule" : "Shoulder emergencies"}</span>
          </nav>

          {/* Critical banner */}
          <div className="mt-6 rounded-2xl border-2 border-[#FF5A1F] bg-[#FF5A1F]/10 p-5 flex items-start gap-4">
            <i className="fa-solid fa-triangle-exclamation text-2xl text-[#FF5A1F] mt-1" aria-hidden="true"></i>
            <div>
              <p className="font-bold text-navy">
                {isFr ? "Urgence vitale ou polytraumatisme : composez le 15 (SAMU)" : "Life-threatening emergency: dial 15 (SAMU)"}
              </p>
              <p className="mt-1 text-sm text-foreground/75">
                {isFr
                  ? "Pour un traumatisme isolé de l'épaule (luxation, fracture suspectée, choc sportif), contactez le secrétariat ou présentez-vous au service d'accueil des urgences le plus proche pour une radiographie."
                  : "For an isolated shoulder injury (dislocation, suspected fracture, sports impact), contact the secretariat or visit the nearest emergency department for an X-ray."}
              </p>
            </div>
          </div>

          <header className="mt-8">
            <span className="chip">{isFr ? "Urgences traumatiques" : "Trauma emergencies"}</span>
            <h1 className="mt-4 font-display font-black text-4xl sm:text-5xl text-navy leading-[1.05]">
              {isFr
                ? "Urgences de l'épaule à Marseille — conduite à tenir"
                : "Shoulder emergencies in Marseille — what to do"}
            </h1>
            <p className="mt-5 text-lg text-foreground/80 leading-relaxed">
              {isFr
                ? "Luxation antérieure récidivante, suspicion de fracture de la clavicule ou du col huméral, chute en VTT ou en moto, traumatisme rugbystique : voici les étapes essentielles pour préserver votre épaule et réduire le délai avant prise en charge spécialisée."
                : "Anterior recurrent dislocation, suspected clavicle or humeral neck fracture, MTB or motorcycle fall, rugby injury: here are the essential steps to preserve your shoulder and reduce the delay before specialized care."}
            </p>
          </header>

          <section className="mt-12">
            <h2 className="font-display text-2xl font-bold text-navy">
              {isFr ? "1. Premiers gestes immédiats" : "1. Immediate first actions"}
            </h2>
            <ul className="mt-4 space-y-3 text-base text-foreground/80">
              <li className="flex gap-3"><i className="fa-solid fa-check text-[#FF5A1F] mt-1" aria-hidden="true"></i>{isFr ? "Immobiliser le bras contre le tronc avec une écharpe ou un foulard." : "Immobilize the arm against the trunk with a sling or scarf."}</li>
              <li className="flex gap-3"><i className="fa-solid fa-check text-[#FF5A1F] mt-1" aria-hidden="true"></i>{isFr ? "Glace 15 minutes toutes les 2 heures (jamais directement sur la peau)." : "Ice 15 minutes every 2 hours (never directly on skin)."}</li>
              <li className="flex gap-3"><i className="fa-solid fa-check text-[#FF5A1F] mt-1" aria-hidden="true"></i>{isFr ? "Ne pas essayer de « remettre » l'épaule en place soi-même : risque vasculo-nerveux." : "Do not try to relocate the shoulder yourself: risk of nerve/vessel injury."}</li>
              <li className="flex gap-3"><i className="fa-solid fa-check text-[#FF5A1F] mt-1" aria-hidden="true"></i>{isFr ? "Antalgiques simples (paracétamol) si pas d'allergie connue." : "Simple analgesia (paracetamol) if no known allergy."}</li>
              <li className="flex gap-3"><i className="fa-solid fa-check text-[#FF5A1F] mt-1" aria-hidden="true"></i>{isFr ? "Réaliser une radiographie d'épaule en urgence (face + profil de Lamy)." : "Get an emergency shoulder X-ray (AP + Lamy view)."}</li>
            </ul>
          </section>

          <section className="mt-12">
            <h2 className="font-display text-2xl font-bold text-navy">
              {isFr ? "2. Signes qui imposent un avis chirurgical rapide" : "2. Signs requiring urgent surgical review"}
            </h2>
            <div className="mt-4 grid sm:grid-cols-2 gap-4">
              {[
                isFr ? "Déformation visible de l'épaule ou de la clavicule" : "Visible shoulder or clavicle deformity",
                isFr ? "Impossibilité totale de bouger le bras" : "Total inability to move the arm",
                isFr ? "Engourdissement, perte de sensibilité du bras ou de la main" : "Numbness, loss of sensation in arm or hand",
                isFr ? "Pâleur, froideur ou pouls absent au poignet" : "Paleness, coldness, absent wrist pulse",
                isFr ? "Plaie ouverte ou saillie osseuse" : "Open wound or bone protrusion",
                isFr ? "Récidive immédiate d'une luxation déjà opérée" : "Immediate re-dislocation after previous surgery",
              ].map((s, i) => (
                <div key={i} className="flex gap-3 rounded-xl border border-[#FF5A1F]/30 bg-[#FF5A1F]/5 p-4">
                  <i className="fa-solid fa-circle-exclamation text-[#FF5A1F] mt-0.5" aria-hidden="true"></i>
                  <p className="text-sm text-foreground/80">{s}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mt-12">
            <h2 className="font-display text-2xl font-bold text-navy">
              {isFr ? "3. Pathologies traitées en urgence" : "3. Conditions treated urgently"}
            </h2>
            <div className="mt-4 grid sm:grid-cols-2 gap-4">
              {[
                { slug: "instabilite-epaule", fr: "Luxation antérieure de l'épaule", en: "Anterior shoulder dislocation" },
                { slug: "fractures-epaule-clavicule", fr: "Fracture de la clavicule", en: "Clavicle fracture" },
                { slug: "fractures-epaule-clavicule", fr: "Fracture du col huméral", en: "Humeral neck fracture" },
                { slug: "disjonction-acromio-claviculaire", fr: "Disjonction acromio-claviculaire", en: "AC joint separation" },
                { slug: "coiffe-rotateurs", fr: "Rupture aiguë de la coiffe", en: "Acute rotator cuff tear" },
                { slug: "plexus-brachial", fr: "Atteinte du plexus brachial", en: "Brachial plexus injury" },
              ].map((p, i) => (
                <Link
                  key={`${p.slug}-${i}`}
                  to={`/pathologie/${p.slug}`}
                  className="group rounded-xl border border-border bg-white p-4 hover:border-[#FF5A1F]/40 hover:shadow-md transition-all"
                  data-testid={`emergency-pathology-${p.slug}-${i}`}
                >
                  <p className="font-display font-bold text-navy group-hover:text-[#FF5A1F] transition-colors">
                    {isFr ? p.fr : p.en}
                  </p>
                </Link>
              ))}
            </div>
          </section>

          <section className="mt-14 rounded-2xl bg-[#0A192F] text-white p-8 md:p-10">
            <h2 className="font-display text-2xl font-bold">
              {isFr ? "Joindre le secrétariat — épaule en urgence" : "Reach the secretariat — urgent shoulder"}
            </h2>
            <p className="mt-3 text-white/80">
              {isFr
                ? "Le secrétariat de l'IMOTEpS organise un créneau d'évaluation rapide pour les urgences sportives et traumatiques de l'épaule. Apportez vos radiographies et votre IRM si déjà réalisées."
                : "The IMOTEpS secretariat arranges a rapid evaluation slot for sports and traumatic shoulder emergencies. Bring your X-rays and MRI if already performed."}
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href={SITE.phoneHref} className="btn-primary">
                <i className="fa-solid fa-phone" aria-hidden="true"></i>
                {SITE.phone} — {isFr ? "RDV" : "Booking"}
              </a>
              <a href={SITE.assistantPhoneHref} className="btn-ghost-light">
                <i className="fa-solid fa-user-tie" aria-hidden="true"></i>
                {SITE.assistantPhone} — {SITE.assistantName}
              </a>
              <Link to="/epaule-du-sportif" className="btn-ghost-light">
                <i className="fa-solid fa-medal" aria-hidden="true"></i>
                {isFr ? "Épaule du sportif" : "Sports shoulder"}
              </Link>
            </div>
          </section>
        </article>
      </main>
    </>
  );
};

export default ShoulderEmergencyPage;
